import React, { useEffect, useState } from "react"
import { Alert, Button, List, Modal, Select, Switch } from "antd"
import { notify } from "shared/ui/Notifications/notify"

interface RunOnLoadModalProps {
  open: boolean
  /** The project's saved scripts, by name: no pipelines or builders. */
  available: string[]
  /** The saved list. */
  list: string[]
  /** Set when the saved list could not be checked or read; the modal shows it as it is. */
  listError?: string | null
  /**
   * Whether a save may replace the saved list although the draft is unchanged:
   * true when the list was read but does not parse, false when it could not
   * be checked (it may be there, so only a changed draft is saved).
   */
  listReplaceable?: boolean
  /** Whether this session shows the data without edits. */
  withoutEdits: boolean
  onClose: () => void
  onSave: (scripts: string[]) => Promise<void>
  /** Why a script cannot go on the list, or null. */
  check: (name: string) => Promise<string | null>
  onReload: () => void
  onToggleWithoutEdits: () => void
}

/**
 * The project's Run on load list: saved scripts that run, in this order,
 * every time the project's data opens. The data file itself never changes.
 * Also this session's Reload and Without edits.
 */
const RunOnLoadModal: React.FC<RunOnLoadModalProps> = ({
  open,
  available,
  list,
  listError = null,
  listReplaceable = false,
  withoutEdits,
  onClose,
  onSave,
  check,
  onReload,
  onToggleWithoutEdits,
}) => {
  const [draft, setDraft] = useState<string[]>(list)
  const [adding, setAdding] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setDraft(list)
      setProblem(null)
      setAdding(null)
    }
  }, [open, list])

  const move = (index: number, by: -1 | 1): void => {
    const next = draft.slice()
    const [item] = next.splice(index, 1)
    next.splice(index + by, 0, item)
    setDraft(next)
  }

  const add = async (): Promise<void> => {
    if (!adding) return
    const refusal = await check(adding)
    if (refusal) {
      setProblem(`"${adding}": ${refusal}`)
      return
    }
    setDraft([...draft, adding])
    setAdding(null)
    setProblem(null)
  }

  const save = async (): Promise<void> => {
    setSaving(true)
    try {
      await onSave(draft)
      onClose()
    } catch (err: unknown) {
      // The modal stays open with the draft, so the save can be tried again.
      notify.error("Run on load", err instanceof Error && err.message ? err.message : "The list could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  // A saved list that was read but does not parse can always be replaced, even by an empty one.
  // One that could not be checked may be there, so only a changed draft is saved over it.
  const changed = (Boolean(listError) && listReplaceable) || draft.join("\n") !== list.join("\n")

  return (
    <Modal
      open={open}
      title="Run on load"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} disabled={!changed} onClick={() => void save()}>
          Save and reload
        </Button>,
      ]}
    >
      <p>
        These saved scripts run, in this order, every time this project&apos;s data opens: in the
        Query Console, the Tables tab, dashboards and connected AI agents. The data file itself never
        changes.
      </p>
      {listError && <Alert className="mb-2" type="warning" showIcon message={listError} />}
      <List
        size="small"
        bordered
        locale={{ emptyText: "No scripts run on load." }}
        dataSource={draft}
        renderItem={(name, index) => (
          <List.Item
            actions={[
              <Button key="up" size="small" aria-label={`Move ${name} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                Up
              </Button>,
              <Button key="down" size="small" aria-label={`Move ${name} down`} disabled={index === draft.length - 1} onClick={() => move(index, 1)}>
                Down
              </Button>,
              <Button key="remove" size="small" danger aria-label={`Remove ${name}`} onClick={() => setDraft(draft.filter((n) => n !== name))}>
                Remove
              </Button>,
            ]}
          >
            {name}
          </List.Item>
        )}
      />
      <div className="flex gap-2 mt-3">
        <Select
          className="flex-1"
          placeholder="Add a saved script"
          aria-label="Saved script to add"
          value={adding ?? undefined}
          onChange={(v: string) => setAdding(v)}
          options={available.filter((n) => !draft.includes(n)).map((n) => ({ value: n, label: n }))}
        />
        <Button disabled={!adding} onClick={() => void add()}>
          Add
        </Button>
      </div>
      {problem && <Alert className="mt-2" type="error" showIcon message={problem} />}
      <div className="flex items-center justify-between mt-4 pt-3 border-t">
        <span>
          <Switch size="small" checked={withoutEdits} onChange={onToggleWithoutEdits} aria-label="Without edits" />{" "}
          Without edits: this session shows the data as imported
        </span>
        <Button size="small" onClick={onReload}>
          Reload data
        </Button>
      </div>
    </Modal>
  )
}

export default RunOnLoadModal
